import { StaticScreenProps } from "@react-navigation/native";

import {
  Col,
  Content,
  Divider,
  Icon,
  Row,
  Surface,
  TextLabel,
  TextLink,
  TextTitle,
} from "../lib";
import {
  getBookingStatus,
  getExpiresIn,
  useBookings,
  UserService,
  useUser,
} from "../services";
import { LoadingScreen } from "./loading";
import {
  BookingDetails,
  Tag,
  Timer,
  Detail,
  CalloutRefresh,
} from "../components";
import { useCallback } from "react";
import { BookingAction } from "../components/booking-action";

const contactProviderNames: Record<string, string> = {
  phone: "Phone",
  line: "LINE",
  telegram: "Telegram",
  whatsapp: "WhatsApp",
};

type Props = StaticScreenProps<{
  id: number;
}>;

export function BookingShowScreen({ route }: Props) {
  // Params
  const { id } = route.params;

  // Data
  const {
    data: user,
    isLoading: isUserLoading,
    refetch: refetchUser,
  } = useUser();
  const {
    data: bookings,
    isLoading: isBookingsLoading,
    refetch: refetchBookings,
  } = useBookings();

  // Vars
  const booking = (bookings ?? []).find((b) => b.id === id) ?? null;

  // Methods
  const refresh = useCallback(() => {
    refetchUser();
    refetchBookings();
  }, [refetchUser, refetchBookings]);

  // Handlers
  const handleCall = useCallback(() => {
    // TODO
  }, []);

  const handleContact = useCallback(() => {
    // TODO
  }, []);

  // Render
  if (isUserLoading || isBookingsLoading) {
    return <LoadingScreen />;
  }

  if (!user || !booking) {
    return <CalloutRefresh onRefresh={refresh} />;
  }

  const allServices = booking.services.concat(booking.offers);

  const phoneLink = (
    <TextLink onPress={handleCall}>
      {booking.phone} <Icon name="open-in-new" small />
    </TextLink>
  );

  const contactLink = (
    <TextLink onPress={handleContact}>
      {booking.user.contact_provider_id} <Icon name="open-in-new" small />
    </TextLink>
  );

  let timer = null;

  if (booking.verified) {
    const initialCount = getExpiresIn(
      new Date(booking.requested_at),
      booking.timeout,
    );

    timer = <Timer value={initialCount} />;
  }

  const discounted = booking.discount_type === "premium";

  let travelFee;

  if (booking.travel_fee) {
    travelFee = booking.travel_fee_formatted;
  }

  return (
    <Content>
      <Surface>
        <Row.LR>
          <TextTitle small>{booking.customer_name}</TextTitle>
          {timer ? timer : <TextLabel>{getBookingStatus(booking)}</TextLabel>}
        </Row.LR>

        <Divider my="4" />

        <BookingDetails
          {...{
            date: new Date(booking.date),
            incall: booking.incall,
            placeId: booking.location.place_id,
            locationName: booking.location.name,
            duration: booking.duration,
            total: booking.total_formatted,
            travelFee,
            discounted,
            timeZone: booking.city.timezone,
          }}
        />

        {!!booking.room_number && (
          <Col mb="4">
            <Detail name="Room number" value={booking.room_number} />
          </Col>
        )}

        <Detail name="Client name" value={booking.customer_name} />
        {!booking.archived && <Detail name="Phone" value={phoneLink} />}
        {!booking.archived &&
          booking.user.contact_provider &&
          booking.user.contact_provider !== "phone" && (
            <Detail
              name={contactProviderNames[booking.user.contact_provider]}
              value={contactLink}
            />
          )}
        {!!booking.notes && <Detail name="Requests" value={booking.notes} />}

        <TextTitle mt="4" mb="2" small>
          Chosen services
        </TextTitle>
        {allServices.map((service) => (
          <BookingService key={service.id} {...service} />
        ))}
        {allServices.length === 0 && <TextLabel>No service</TextLabel>}

        <Divider my="4" />

        <Row.L>
          <TextTitle small>{booking.total_formatted}</TextTitle>
          {discounted && <Tag label="Premium price" />}
        </Row.L>

        <Col mt="4">
          <BookingAction booking={booking} />
        </Col>
      </Surface>
    </Content>
  );
}

function BookingService({ name, paid, price_formatted: price }: UserService) {
  return (
    <Row.L mb="1">
      <Tag label={name} />
      <Col px="4">
        <TextLabel>{paid ? price : "Free"}</TextLabel>
      </Col>
    </Row.L>
  );
}
